import React, { useState, useEffect, useCallback, useMemo } from "react";
import { displayName, useMaskNames } from "./nameMask";

const API = "https://api.pharmacegroup.co.uk";

/**
 * Suppliers, and each branch's account number with them.
 *
 * Master and detail, because that is the shape of the thing: a wholesaler has
 * one name and one email domain across the whole estate, but a different
 * account number at every branch. Listing domains and account numbers as two
 * separate tables made the reader hold that relationship in their head, and
 * left "which of my branches has an account with this supplier" as a question
 * the screen could not answer.
 *
 * The supplier record is therefore the template: enter the name and domain
 * once, then fill in an account number per branch beside it.
 *
 * An organisation manages its own supplier entries and its own branches'
 * accounts. Suppliers shared across the estate are shown but not editable
 * here - one organisation changing a shared domain would silently change
 * routing for every other.
 */
const InvoiceSuppliers = () => {
  const masked = useMaskNames();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(null);   // supplier name
  const [editSup, setEditSup] = useState(null);
  const [drafts, setDrafts] = useState({});         // pharmacyId -> typed value
  const [placed, setPlaced] = useState(null);       // what a save just released

  const token = localStorage.getItem("authToken");

  const post = useCallback(async (path, body = {}) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
    return j;
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await post("/api/invoiceAnalysis/supplierSetup")); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [post]);

  useEffect(() => { load(); }, [load]);

  const act = async (label, fn) => {
    setBusy(label); setError(null);
    try { await fn(); await load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  /** Suppliers, each carrying its accounts and anything awaiting assignment. */
  const suppliers = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const s of data.suppliers) {
      map.set(s.name, { ...s, accounts: [], suggestions: [] });
    }
    // An account naming a supplier we hold no record for still has to appear,
    // or it is invisible rather than obviously missing.
    const ensure = (name) => {
      const key = name || "(supplier not identified)";
      if (!map.has(key)) {
        map.set(key, {
          _id: null, name: key, emailDomains: [], shared: false,
          editable: !!name, unnamed: !name, accounts: [], suggestions: [],
        });
      }
      return map.get(key);
    };
    for (const a of data.accounts) ensure(a.supplierName).accounts.push(a);
    for (const u of (data.unmatchedAccounts || [])) ensure(u.supplierName).suggestions.push(u);

    return [...map.values()].sort((a, b) => {
      // whatever needs attention first: unassigned accounts, then no domain
      // needing attention first: unassigned accounts, then suppliers seen on
      // invoices but never set up, then those with no domain
      const rank = (x) => (x.suggestions.length ? 0
                         : x.fromInvoices ? 1
                         : x.emailDomains.length ? 3 : 2);
      return rank(a) - rank(b) || a.name.localeCompare(b.name);
    });
  }, [data]);

  const current = suppliers.find((s) => s.name === selected) || null;
  const branches = data?.branches || [];

  // reset typed-but-unsaved values when moving to another supplier, so a number
  // meant for one wholesaler cannot appear under another
  useEffect(() => { setDrafts({}); }, [selected]);

  const S = {
    card: { border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff" },
    head: { padding: "8px 11px", background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0", fontWeight: "bold", fontSize: 13 },
    th: { textAlign: "left", padding: "6px 8px", fontSize: 11.5, color: "#475569" },
    td: { padding: "6px 8px", fontSize: 12.5 },
    mono: { fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12 },
    btn: (bg) => ({ background: bg || "#fff", color: bg ? "#fff" : "#0f172a",
                    border: "1px solid #cbd5e1", borderRadius: 4,
                    padding: "5px 10px", marginRight: 6, cursor: "pointer",
                    fontSize: 12.5 }),
  };

  /** Save, clear or update one branch's account with the selected supplier. */
  const saveAccount = (branch, value) => act("acct", async () => {
    const supplierName = current.unnamed ? "" : current.name;
    const existing = current.accounts.find((a) => a.pharmacyId === branch.pharmacyId);
    const v = String(value || "").trim();

    if (!v) {
      // An emptied box means "this branch has no account with them", which is
      // a real answer and has to remove the mapping rather than be ignored.
      if (existing) {
        await post("/api/invoiceAnalysis/saveAccount", { _id: existing._id, remove: true });
      }
      return;
    }
    const r = await post("/api/invoiceAnalysis/saveAccount", {
      _id: existing?._id, supplierName, accountNumber: v,
      pharmacyId: branch.pharmacyId,
    });

    // Say what it did to the invoices already waiting. Otherwise the mapping
    // saves silently and the reader has no way of knowing whether the invoice
    // that sent them here has been dealt with.
    if (r.invoicesPlaced > 0) {
      setPlaced(`${r.invoicesPlaced} invoice${r.invoicesPlaced === 1 ? "" : "s"} ` +
                `on account ${v} placed at ${branch.pharmacyName}.`);
    }
  });

  return (
    <div style={{ padding: 4 }}>
      <p style={{ fontSize: "0.9em", color: "#555", maxWidth: 940, marginTop: 4 }}>
        How an emailed invoice is matched to a branch. The account number is the
        reliable route &mdash; it is printed on every invoice and the wholesaler
        assigns one per branch, so it works however the email reaches us. The
        email domain is how we tell whose invoice it is.
      </p>

      {error && (
        <div style={{ background: "#fff4f4", border: "1px solid #e0b4b4",
                      padding: "8px 10px", marginBottom: 10 }}>{error}</div>
      )}
      {loading && <div style={{ padding: 10 }}>Loading&hellip;</div>}
      {placed && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0",
                      padding: "8px 10px", marginBottom: 10, fontSize: 12.5,
                      display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#15803d" }}>{placed}</span>
          <button style={{ ...S.btn(), marginRight: 0, padding: "2px 8px" }}
            onClick={() => setPlaced(null)}>Dismiss</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>

        {/* ------------------------------------------------ supplier list */}
        <div style={{ flex: "0 0 300px", ...S.card }}>
          <div style={S.head}>Suppliers</div>

          {suppliers.map((s) => {
            const on = s.name === selected;
            return (
              <div key={s.name} onClick={() => setSelected(s.name)}
                style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9",
                         cursor: "pointer",
                         background: on ? "#e8f4ef" : "transparent",
                         borderLeft: on ? "4px solid #177245" : "4px solid transparent" }}>
                <div style={{ fontWeight: "bold", fontSize: 13 }}>
                  {s.name}
                  {s.shared && (
                    <span style={{ fontWeight: "normal", color: "#94a3b8", fontSize: 11 }}>
                      {"  "}central
                    </span>
                  )}
                </div>
                <div style={{ ...S.mono, fontSize: 11,
                              color: s.emailDomains.length ? "#64748b" : "#a05a00" }}>
                  {s.emailDomains.join(", ")
                    || (s.fromInvoices ? "not set up yet" : "no email domain")}
                </div>
                <div style={{ fontSize: 11, color: "#64748b" }}>
                  {s.accounts.length} of {branches.length} branches
                  {s.suggestions.length > 0 && (
                    <b style={{ color: "#a05a00" }}>
                      {"  "}· {s.suggestions.length} to assign
                    </b>
                  )}
                </div>
              </div>
            );
          })}

          {!loading && suppliers.length === 0 && (
            <div style={{ padding: 12, color: "#64748b", fontSize: 12.5 }}>
              None yet. Add the wholesalers you buy from.
            </div>
          )}

          <div style={{ padding: 10 }}>
            <button style={S.btn()}
              onClick={() => { setEditSup({ name: "", emailDomains: "", isNew: true });
                               setSelected(null); }}>
              + Add supplier
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------- the detail */}
        <div style={{ flex: 1, ...S.card, minHeight: 220 }}>
          {editSup?.isNew && (
            <>
              <div style={S.head}>New supplier</div>
              <div style={{ padding: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <label style={{ flex: 1, minWidth: 180, fontSize: 12.5 }}>Name
                  <input value={editSup.name} autoFocus
                    style={{ width: "100%", padding: 6 }}
                    onChange={(e) => setEditSup({ ...editSup, name: e.target.value })} />
                </label>
                <label style={{ flex: 2, minWidth: 240, fontSize: 12.5 }}>
                  Email domains, comma separated
                  <input value={editSup.emailDomains} placeholder="aah.co.uk"
                    style={{ width: "100%", padding: 6 }}
                    onChange={(e) => setEditSup({ ...editSup, emailDomains: e.target.value })} />
                </label>
                <div style={{ alignSelf: "flex-end" }}>
                  <button style={S.btn("#16a34a")} disabled={busy === "sup" || !editSup.name}
                    onClick={() => act("sup", async () => {
                      await post("/api/invoiceAnalysis/saveSupplier", editSup);
                      setSelected(editSup.name);
                      setEditSup(null);
                    })}>Add</button>
                  <button style={S.btn()} onClick={() => setEditSup(null)}>Cancel</button>
                </div>
              </div>
              <div style={{ padding: "0 12px 12px", fontSize: 12, color: "#64748b" }}>
                Once added, put each branch&rsquo;s account number against it.
              </div>
            </>
          )}

          {!editSup?.isNew && !current && (
            <div style={{ padding: 18, color: "#64748b" }}>
              Choose a supplier to see each branch&rsquo;s account number with them.
            </div>
          )}

          {!editSup?.isNew && current && (
            <>
              <div style={{ ...S.head, display: "flex", justifyContent: "space-between",
                            alignItems: "center" }}>
                <span>{current.name}</span>
                {current.editable && !current.unnamed && (
                  <button style={{ ...S.btn(), marginRight: 0 }}
                    onClick={() => setEditSup({
                      _id: current._id, name: current.name,
                      emailDomains: current.emailDomains.join(", "),
                    })}>
                    Edit name and domains
                  </button>
                )}
              </div>

              {/* --- the shared part: name and domain --- */}
              <div style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                {editSup && !editSup.isNew ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <label style={{ flex: 1, minWidth: 160, fontSize: 12.5 }}>Name
                      <input value={editSup.name} style={{ width: "100%", padding: 5 }}
                        onChange={(e) => setEditSup({ ...editSup, name: e.target.value })} />
                    </label>
                    <label style={{ flex: 2, minWidth: 240, fontSize: 12.5 }}>
                      Email domains, comma separated
                      <input value={editSup.emailDomains} style={{ width: "100%", padding: 5 }}
                        onChange={(e) => setEditSup({ ...editSup, emailDomains: e.target.value })} />
                    </label>
                    <div style={{ alignSelf: "flex-end" }}>
                      <button style={S.btn("#16a34a")} disabled={busy === "sup"}
                        onClick={() => act("sup", async () => {
                          await post("/api/invoiceAnalysis/saveSupplier", editSup);
                          setSelected(editSup.name);
                          setEditSup(null);
                        })}>Save</button>
                      <button style={S.btn()} onClick={() => setEditSup(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5 }}>
                    <b>Sends invoices from:</b>{" "}
                    <span style={{ ...S.mono,
                                   color: current.emailDomains.length ? "#0f172a" : "#a05a00" }}>
                      {current.emailDomains.join(", ") || "not set"}
                    </span>
                    <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 2 }}>
                      Only the part after the @, since the address itself varies.
                      The same for every branch.
                    </div>
                    {current.fromInvoices && (
                      <div style={{ color: "#a05a00", fontSize: 11.5, marginTop: 4 }}>
                        Taken from invoices that have arrived &mdash; this
                        supplier has no record yet. Saving a domain creates one.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* --- accounts already seen but not yet placed --- */}
              {current.suggestions.length > 0 && (
                <div style={{ margin: "10px 12px", background: "#fffaf0",
                              border: "1px solid #f0dcae", padding: 8, fontSize: 12.5 }}>
                  <b>Seen on invoices, not yet assigned</b>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    Type one against the right branch below and it will be used
                    from then on.
                  </div>
                  {current.suggestions.map((u, i) => (
                    <div key={i} style={{ marginTop: 3 }}>
                      <span style={S.mono}>{u.accountNumber}</span>
                      <span style={{ color: "#64748b" }}>
                        {" "}&times; {u.count}
                        {u.branchHint ? ` — the invoice says ${u.branchHint}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* --- the per-branch part --- */}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#fafafa" }}>
                  <th style={S.th}>Branch</th>
                  <th style={S.th}>Account number</th>
                  <th style={S.th}></th>
                </tr></thead>
                <tbody>
                  {branches.map((b) => {
                    const hit = current.accounts.find((a) => a.pharmacyId === b.pharmacyId);
                    const saved = hit?.accountNumber || "";
                    const value = drafts[b.pharmacyId] ?? saved;
                    return (
                      <tr key={b.pharmacyId} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={S.td}>{displayName(b.pharmacyName, masked)}</td>
                        <td style={S.td}>
                          <input value={value} placeholder="no account"
                            style={{ ...S.mono, padding: 5, width: 190 }}
                            onChange={(e) =>
                              setDrafts({ ...drafts, [b.pharmacyId]: e.target.value })}
                            onBlur={(e) => {
                              // saved on leaving the box, and only when it
                              // actually changed - re-saving an untouched value
                              // would churn the record for nothing
                              if (e.target.value.trim() === saved) return;
                              saveAccount(b, e.target.value);
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          />
                        </td>
                        <td style={{ ...S.td, fontSize: 11.5, color: "#64748b" }}>
                          {hit?.learned ? "learned from a delivery" : ""}
                        </td>
                      </tr>
                    );
                  })}
                  {branches.length === 0 && (
                    <tr><td colSpan={3} style={{ ...S.td, color: "#64748b" }}>
                      No branches visible for this organisation.
                    </td></tr>
                  )}
                </tbody>
              </table>

              <div style={{ padding: "8px 12px", fontSize: 11.5, color: "#64748b" }}>
                Saved when you leave the box. Clearing one records that the
                branch has no account with this supplier.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceSuppliers;